<template>
  <div
    v-if="readyToRender"
    :class="['studio-feed-row', {
      'is-editor-preview': editMode,
      'size-to-card': sizeToCard,
      'fill-card': sizeToCard && cardFillsWidth,
      'width-hug': sizeToCard && !cardFillsWidth,
    }]"
    @click.capture="handleEditorPreviewClick"
  >
    <component
      :is="component"
      :key="rowRenderKey"
      :dynamic-component-id="dynamicComponentId"
      :is-production="rowIsProduction"
      :theme-mode="themeMode"
      :use-example-data="editMode"
    />
  </div>
  <iText
    v-else-if="nestsItself && !renderingInProduction"
    variant="subtle"
    font-size="small"
    data-test="feed-row-nests-itself"
  >
    This card holds a feed drawn with itself, so it isn't drawn again here.
  </iText>
</template>

<script>
import { markRaw } from "vue";
import App from "@bloglovin/inspire-engine/App";
import { isCompiledRenderSurface, isBuilderAppBooted, contextualAppName }
  from "%/dynamic-component-editor/classes/platform-standard-entities";
import { loadCompiledBundle } from "%/dynamic-components/render-engine/compiled-payload-fetch";
import { getCompiledDcMeta, mergeCompiledBundle } from "%/dynamic-components/render-engine/compiled-payload-store";
import { feedRowRenderKey } from "%/dynamic-component-editor/standard-elements/feed-row-render-key";
import DynamicComponent from "%/dynamic-component-editor/classes/dynamic-component";
import { feedSeparatorLabelContextName } from "%/dynamic-component-editor/helpers/feed-separator-label";
import { isRowNestedInItself, rowAncestryWith } from "%/dynamic-component-editor/standard-elements/feed-row-nesting";

const reportedSelfNestingCards = new Set();

export default {
  name: "StudioFeedRowItem",
  inject: {
    pageEntities: { default: null },
    renderingInProduction: { default: false },
    feedRowAncestry: { default: null },
  },
  provide() {
    const provides = {
      useExampleData: () => Boolean(this.editMode),
      scopedProvidedContexts: () => this.rowScopedContexts,
      feedRowAncestry: () => rowAncestryWith(this.ancestorComponentIds, this.dynamicComponentId),
      inFeedRow: true,
    };
    if (this.contextName) {
      provides.pageEntities = () => {
        const parent = typeof this.pageEntities === "function"
          ? this.pageEntities()
          : this.pageEntities ?? {};
        return { ...parent, [this.contextName]: this.entityId };
      };
    }
    return provides;
  },
  props: {
    entityId: {
      type: [Number, String],
      required: true,
    },
    dynamicComponentId: {
      type: Number,
      required: true,
    },
    contextName: {
      type: String,
      required: false,
      default: "",
    },
    separatorLabel: {
      type: String,
      required: false,
      default: "",
    },
    editMode: {
      type: Boolean,
      required: false,
      default: false,
    },
    scopedContexts: {
      type: Object,
      required: false,
      default: null,
    },
    sizeToCard: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      component: null,
      mountedDc: null,
      readyToRender: false,
      cardFillsWidth: false,
    };
  },
  computed: {
    ancestorComponentIds() {
      return typeof this.feedRowAncestry === "function" ? this.feedRowAncestry() : [];
    },
    nestsItself() {
      return isRowNestedInItself(this.ancestorComponentIds, this.dynamicComponentId);
    },
    rowScopedContexts() {
      return this.scopedContexts
        ? { ...this.scopedContexts, ...this.separatorScopedContexts }
        : this.separatorScopedContexts;
    },
    separatorScopedContexts() {
      const contextName = feedSeparatorLabelContextName(
        DynamicComponent.get(Number(this.dynamicComponentId)),
      );
      if (!contextName) {
        return {};
      }
      return { [contextName]: this.separatorLabel };
    },
    rowIsProduction() {
      if (this.editMode) {
        return false;
      }
      return Boolean(this.renderingInProduction);
    },
    referencedDcVersion() {
      if (isCompiledRenderSurface() || !isBuilderAppBooted() || !this.dynamicComponentId) {
        return null;
      }
      const field = App.get(contextualAppName())
          .getEntityField("DynamicComponent", { id: String(this.dynamicComponentId) }, "currentVersion");
      const id = Number(field?.pk?.id ?? field?.id ?? field);
      return Number.isFinite(id) ? id : null;
    },
    rowRenderKey() {
      return feedRowRenderKey(this.dynamicComponentId, this.referencedDcVersion);
    },
  },
  watch: {
    dynamicComponentId() {
      this.readyToRender = false;
      this.mountRowDc();
    },
  },
  created() {
    this.mountRowDc();
  },
  methods: {
    async mountRowDc() {
      const targetId = this.dynamicComponentId;
      if (this.nestsItself) {
        this.reportSelfNesting(targetId);
        return;
      }
      const [{ default: DynamicComponent }, { default: ProductionComponent }] = await Promise.all([
        import("%/dynamic-component-editor/classes/dynamic-component"),
        import("%/dynamic-component-editor/components/DynamicComponentProductionComponent.vue"),
      ]);
      if (this.dynamicComponentId !== targetId) {
        return;
      }
      await this.ensureRowCompiledTree(targetId);
      if (this.dynamicComponentId !== targetId) {
        return;
      }
      const dc = DynamicComponent.get(targetId, this.rowIsProduction);
      dc.setup(0, "", [], {});
      this.mountedDc = dc;
      this.component = markRaw(ProductionComponent);
      await this.waitForResolvedRoot(dc, targetId);
      if (this.dynamicComponentId !== targetId) {
        return;
      }
      this.cardFillsWidth = await this.resolveCardFillsWidth(dc);
      this.readyToRender = true;
    },
    reportSelfNesting(dcId) {
      if (reportedSelfNestingCards.has(dcId)) {
        return;
      }
      reportedSelfNestingCards.add(dcId);
      console.error( // eslint-disable-line no-console
        `StudioFeedRowItem: component ${dcId} is drawn inside itself: it holds a feed whose rows are drawn with `
        + `component ${dcId} (chain ${this.ancestorComponentIds.join(" > ")} > ${dcId}). The nested copy is not `
        + "drawn, since drawing it would nest forever; change that feed's row template or remove it from the component.",
      );
    },
    ensureRowCompiledTree(dcId) {
      if (!isCompiledRenderSurface() || getCompiledDcMeta(dcId)) {
        return Promise.resolve();
      }
      const destinationId = window.blclient?.data?.themeDestinationId ?? 0;
      if (!destinationId || !dcId) {
        return Promise.resolve();
      }
      const env = this.rowIsProduction ? "destination" : "staging";
      const descriptorMap = window.blclient?.data?.[
        this.rowIsProduction ? "productionCompiledDescriptors" : "stagingCompiledDescriptors"
      ] ?? {};
      const version = descriptorMap[dcId]?.version;
      return loadCompiledBundle(env, { dynamicComponentId: Number(dcId), version })
          .then(({ bundle }) => {
            if (bundle?.payload) {
              mergeCompiledBundle(bundle);
            }
          });
    },
    async resolveCardFillsWidth(dc) {
      if (!this.sizeToCard) {
        return false;
      }
      const rootElementId = dc?.rootElementId;
      if (rootElementId === null || rootElementId === undefined) {
        return false;
      }
      const { default: DynamicElement } = await import("%/dynamic-component-editor/classes/dynamic-element");
      const rootElement = await this.waitForRegisteredElement(DynamicElement, String(rootElementId));
      return rootElement?.getCompiledOptionValue("layout-width", "default", "default", this.resolvedThemeMode) === "fill";
    },
    async waitForRegisteredElement(DynamicElement, rootElementId) {
      for (let attempt = 0; attempt < 50; attempt++) {
        const rootElement = DynamicElement.get(rootElementId);
        if (rootElement) {
          return rootElement;
        }
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      return null;
    },
    async waitForResolvedRoot(dc, targetId) {
      for (let attempt = 0; attempt < 50; attempt++) {
        if (this.dynamicComponentId !== targetId) {
          return;
        }
        const rootElementId = dc.rootElementId;
        if (rootElementId !== null && rootElementId !== undefined) {
          return;
        }
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    },
    handleEditorPreviewClick(event) {
      if (!this.editMode) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
    },
  },
};
</script>

<style scoped lang="scss">
.studio-feed-row {
  width: 100%;
}

.studio-feed-row.size-to-card {
  width: fit-content;
}

.studio-feed-row.size-to-card.fill-card {
  width: 100%;
}
</style>
